// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Badge,
    Button,
    Form,
    List,
    Popover,
    Section,
    Switch,
    Toolbar,
    ToolbarGroup,
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import {
    IComputeResourceResponse,
    VirtualizationType,
} from 'common/api/resources/ComputeResource/model';
import { Status } from 'admin/computeResource/common/Status/Status';
import { HaStatus } from 'admin/computeResource/common/HaStatus/HaStatus';
import { getStatus } from 'common/api/resources/ComputeResource';
import { ActionsCell } from 'common/components/ActionsCell/ActionsCell';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { getActionColumnProps } from 'common/helpers/list';
import { ICONS } from 'common/constants';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import CopyText from 'common/containers/CopyText/CopyText';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { StyledTable } from 'common/components/styles/StyledTable';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import {
    HostRam,
    RamCell,
    RamUsage,
    ServersCell,
    VirtualizationType as VtType,
} from 'admin/failoverDomain/page/components/ComputeResourceTable/Styles';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectRequiredOption } from 'common/components';
import { FixedFormField } from 'admin/common/components/Filters/Styles';
import {
    COMPUTE_RESOURCE_HA_STATUS,
    COMPUTE_RESOURCE_STATUS,
} from 'admin/computeResource/constants';
import { VerticalFiltersContainer } from 'admin/common/styles/Styles';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import { getComputeResourceHostRamGiB } from 'common/helpers/computeResource';
import { PropsValue } from 'react-select';

const statusTranslationKeys: Record<COMPUTE_RESOURCE_STATUS, string> = {
    [COMPUTE_RESOURCE_STATUS.ACTIVE]: 'computeResource.statuses.active',
    [COMPUTE_RESOURCE_STATUS.COMMISSIONING]: 'computeResource.statuses.commissioning',
    [COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK]: 'computeResource.statuses.configureNetwork',
    [COMPUTE_RESOURCE_STATUS.CONFIGURING_NETWORK]: 'computeResource.statuses.configuringNetwork',
    [COMPUTE_RESOURCE_STATUS.FAILED]: 'computeResource.statuses.failed',
    [COMPUTE_RESOURCE_STATUS.UNAVAILABLE]: 'computeResource.statuses.unavailable',
};

const haStatusTranslationKeys: Record<COMPUTE_RESOURCE_HA_STATUS, string> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: 'computeResource.haStatuses.protected',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: 'computeResource.haStatuses.notEligible',
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: 'computeResource.haStatuses.eligible',
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: 'computeResource.haStatuses.paused',
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: 'computeResource.haStatuses.atRisk',
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: 'computeResource.haStatuses.unavailable',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: 'computeResource.haStatuses.notProtected',
};

// RAM provisioned by this CR's servers, above the host's own memory as muted context.
//
// The two are deliberately not shown as one ratio: `limits.ram` is an allocation ceiling the
// admin sets, so a CR without one has no denominator to divide by, and provisioned RAM may
// legitimately exceed physical memory. Sizes are rendered exactly rather than rounded so that
// over-provisioning stays visible instead of rounding into a tidy-looking match.
//
// `ram.used` is a cached snapshot: the backend recomputes it from VM specifications on a
// five-minute schedule (`ComputeResourceUpdateUsages`), so it lags server creation and deletion,
// while the host memory below it refreshes on the one-minute health check.
const renderRamUsage = (computeResource: IComputeResourceResponse) => {
    const { ram } = computeResource.settings.limits;
    const provisioned = convertToDataUnit(ram.used ?? 0, DataUnit.GiB);
    const hostRamGiB = getComputeResourceHostRamGiB(computeResource);

    return (
        <RamCell>
            <RamUsage>
                {ram.unlimited ? (
                    <>
                        {provisioned} {DataUnit.GiB} /{' '}
                        <Translate content="computeResource.settings.form.limits.unlimited" />
                    </>
                ) : (
                    <>
                        {provisioned} / {convertToDataUnit(ram.total, DataUnit.GiB)} {DataUnit.GiB}
                    </>
                )}
            </RamUsage>
            {hostRamGiB > 0 && (
                <HostRam>
                    <Translate content="failoverDomain.page.ram.host" params={{ size: hostRamGiB }} />
                </HostRam>
            )}
        </RamCell>
    );
};

const columns = [{
    width: '1%',
    key: 'colId',
    title: <Translate content="computeResource.list.id" />,
}, {
    key: 'colName',
    title: <Translate content="failoverDomain.page.computeResourceColumn" />,
}, {
    key: 'colStatus',
    title: <Translate content="computeResource.list.status" />,
}, {
    key: 'colHaStatus',
    title: <Translate content="computeResource.list.haReadinessStatus" />,
}, {
    key: 'colVersion',
    title: <Translate content="computeResource.list.version" />,
}, {
    key: 'colServers',
    title: <Translate content="computeResource.list.serversAndHa" />,
}, {
    key: 'colRamUsage',
    title: <Translate content="computeResource.list.ramUsage" />,
}, {
    width: '1%',
    key: 'colLocked',
    title: <Translate content="computeResource.list.isLocked" />,
}, {
    width: '1%',
    key: 'colMaintenance',
    title: <Translate content="computeResource.list.isInMaintenance" />,
    truncate: true,
}, getActionColumnProps(),
];

export interface IComputeResourceTableProps extends RouteComponentProps {
    items: IComputeResourceResponse[];
    onExclude: (crId: number) => void;
    updateIsLocked: (id: number, isLocked: boolean) => Promise<unknown>;
    updateIsInMaintenance: (id: number, isInMaintenance: boolean) => Promise<unknown>;
    loadMore: () => void;
    hasMore: boolean;
}

export const ComputeResourceTable: React.FC<IComputeResourceTableProps> = ({
    items,
    onExclude,
    updateIsLocked,
    updateIsInMaintenance,
    loadMore,
    hasMore,
    history,
}) => {
    const translate = useTranslate();

    const [lockingChanging, setLockingChanging] = React.useState<number[]>([]);
    const [maintenanceModeChanging, setMaintenanceModeChanging] = React.useState<number[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<PropsValue<ISelectRequiredOption>>(null);
    const [selectedHaStatus, setSelectedHaStatus] = React.useState<PropsValue<ISelectRequiredOption>>(null);

    const handleToggleIsLocked = (cr: IComputeResourceResponse) => async () => {
        if (lockingChanging.includes(cr.id)) {
            return;
        }

        setLockingChanging(prev => [...prev, cr.id]);
        try {
            await updateIsLocked(cr.id, !cr.is_locked);
        } finally {
            setLockingChanging(prev => prev.filter(id => id !== cr.id));
        }
    };

    const handleToggleIsInMaintenance = (cr: IComputeResourceResponse) => async () => {
        if (maintenanceModeChanging.includes(cr.id)) {
            return;
        }

        setMaintenanceModeChanging(prev => [...prev, cr.id]);
        try {
            await updateIsInMaintenance(cr.id, !cr.is_in_maintenance);
        } finally {
            setMaintenanceModeChanging(prev => prev.filter(id => id !== cr.id));
        }
    };

    const statusOptions: ISelectRequiredOption[] = React.useMemo(() =>
        Object.values(COMPUTE_RESOURCE_STATUS).map(status => ({
            value: status,
            label: translate(statusTranslationKeys[status]) as string,
        })),
    [translate]);

    const haStatusOptions: ISelectRequiredOption[] = React.useMemo(() =>
        Object.values(COMPUTE_RESOURCE_HA_STATUS).map(status => ({
            value: status,
            label: translate(haStatusTranslationKeys[status]) as string,
        })),
    [translate]);

    const filteredCount = React.useMemo(
        () => [selectedStatus, selectedHaStatus].filter(Boolean).length,
        [selectedStatus, selectedHaStatus]
    );

    const filteredItems = React.useMemo(() => {
        let result = items;

        if (selectedStatus) {
            const statusValue = (selectedStatus as ISelectRequiredOption).value;
            result = result.filter(item => getStatus(item.status) === statusValue);
        }

        if (selectedHaStatus) {
            const haStatusValue = (selectedHaStatus as ISelectRequiredOption).value;
            result = result.filter(item => item.ha_readiness_status === haStatusValue);
        }

        return result;
    }, [items, selectedStatus, selectedHaStatus]);

    const data = filteredItems.map(item => ({
        colId: item.id,
        colName: (
            <div>
                <Link to={pathTo(`compute_resources/${item.id}`)} style={{ fontWeight: 600 }}>
                    {item.name}
                </Link>
                <div>
                    <CopyText>{item.host}</CopyText>
                </div>
            </div>
        ),
        colStatus: <Status status={getStatus(item.status)} withoutButton={true} />,
        colHaStatus: item.ha_readiness_status ? (
            <HaStatus status={item.ha_readiness_status} />
        ) : null,
        colVersion: item.version,
        colServers: (
            <ServersCell>
                <span>{item.vms_count} ({item.high_availability_enabled_servers_count ?? 0})</span>
                {item.settings.virtualization_types.length > 0 && (
                    <VtType>
                        {item.settings.virtualization_types.map((t: VirtualizationType) => t.toUpperCase()).join('/')}
                    </VtType>
                )}
            </ServersCell>
        ),
        colRamUsage: renderRamUsage(item),
        colLocked: (
            <Switch
                intent="danger"
                checked={item.is_locked}
                onChange={handleToggleIsLocked(item)}
                loading={lockingChanging.includes(item.id)}
            />
        ),
        colMaintenance: (
            <Switch
                intent="danger"
                checked={item.is_in_maintenance}
                onChange={handleToggleIsInMaintenance(item)}
                loading={maintenanceModeChanging.includes(item.id)}
            />
        ),
        colActions: (
            <ActionsCell
                triggerTooltip={<Translate content="computeResource.actions.menu" />}
                actions={[
                    {
                        icon: ICONS.PENCIL,
                        label: <Translate content="computeResource.actions.edit" />,
                        onClick: () => history.push(pathTo(`compute_resources/${item.id}`)),
                    },
                    {
                        icon: ICONS.GEAR,
                        label: <Translate content="computeResource.actions.settings" />,
                        onClick: () => history.push(pathTo(`compute_resources/${item.id}#settings`)),
                    },
                    {
                        icon: ICONS.RECYCLE,
                        label: <Translate content="failoverDomain.page.excludeFromDomain" />,
                        confirmation: {
                            handleConfirm: () => onExclude(item.id),
                            button: <Translate content="failoverDomain.page.excludeConfirmation.button" />,
                            title: <Translate content="failoverDomain.page.excludeConfirmation.title" />,
                            tooltip: <Translate content="failoverDomain.page.excludeFromDomain" />,
                        },
                    },
                ]}
            />
        ),
        key: item.id.toString(),
    }));

    return (
        <StyledTable verticalAlign="top">
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore}>
                <Toolbar>
                    <ToolbarGroup title="filters">
                        <Popover
                            canCloseOnOutsideClick={false}
                            target={(
                                <Badge label={filteredCount} hidden={!filteredCount}>
                                    <Button icon={ICONS.FILTER}>
                                        <Translate content="failoverDomain.page.filter" />
                                    </Button>
                                </Badge>
                            )}
                            placement="left-bottom"
                        >
                            <Section title={<Translate content="failoverDomain.page.filter" />}>
                                <Form
                                    submitButton={false}
                                    cancelButton={false}
                                    applyButton={false}
                                    vertical={true}
                                    footerClassName="hidden"
                                >
                                    <VerticalFiltersContainer>
                                        <FixedFormField>
                                            <SelectInput<ISelectRequiredOption>
                                                inputId="cr-status-filter"
                                                placeholder={<Translate content="computeResource.list.status" />}
                                                options={statusOptions}
                                                isClearable={true}
                                                onChange={setSelectedStatus}
                                                value={selectedStatus}
                                                menuPosition="absolute"
                                                menuPortalTarget={document.body}
                                            />
                                        </FixedFormField>
                                        <FixedFormField>
                                            <SelectInput<ISelectRequiredOption>
                                                inputId="cr-ha-status-filter"
                                                placeholder={<Translate content="computeResource.list.haReadinessStatus" />}
                                                options={haStatusOptions}
                                                isClearable={true}
                                                onChange={setSelectedHaStatus}
                                                value={selectedHaStatus}
                                                menuPosition="absolute"
                                                menuPortalTarget={document.body}
                                            />
                                        </FixedFormField>
                                    </VerticalFiltersContainer>
                                </Form>
                            </Section>
                        </Popover>
                    </ToolbarGroup>
                </Toolbar>
                <List
                    emptyView={
                        <EmptyView
                            title="failoverDomain.page.emptyComputeResources.title"
                            description="failoverDomain.page.emptyComputeResources.description"
                            icon={ICONS.RESOURCE}
                        />
                    }
                    columns={columns}
                    data={data}
                />
            </InfiniteScroll>
        </StyledTable>
    );
};

export default withRouter(ComputeResourceTable);
